import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import './LoadFromBackendDialog.scss';

const LoadFromBackendDialog = ({
  isOpen,
  onClose,
  onSelect,
  getComponent,
  backendSpecsActions,
  backendSpecsSelectors,
}) => {
  const [selectedSpecId, setSelectedSpecId] = useState(null);
  const Modal = getComponent('Modal', true);
  const ModalHeader = getComponent('ModalHeader', true);
  const ModalTitle = getComponent('ModalTitle', true);
  const ModalBody = getComponent('ModalBody', true);
  const ModalFooter = getComponent('ModalFooter', true);

  const specsList = backendSpecsSelectors?.selectSpecsList?.() || [];
  const isLoading = backendSpecsSelectors?.selectSpecsListLoading?.() || false;
  const error = backendSpecsSelectors?.selectSpecsListError?.() || null;

  useEffect(() => {
    if (isOpen && backendSpecsActions?.fetchSpecsList) {
      backendSpecsActions.fetchSpecsList();
      setSelectedSpecId(null);
    }
  }, [isOpen, backendSpecsActions]);

  const handleSpecClick = useCallback((specId) => {
    setSelectedSpecId(specId);
  }, []);

  const handleLoadClick = useCallback(() => {
    if (selectedSpecId) {
      // Find the selected spec and pass full info
      const selectedSpec = specsList.find((spec) => spec.id === selectedSpecId);
      console.log('LoadFromBackendDialog: Loading spec', {
        selectedSpecId,
        selectedSpec,
        specsList,
      });
      onSelect(selectedSpecId, selectedSpec);
    }
  }, [selectedSpecId, specsList, onSelect]);

  const handleCancelClick = useCallback(() => {
    onClose(false);
  }, [onClose]);

  if (!isOpen || !Modal) return null;

  return (
    <Modal isOpen={isOpen} contentLabel="Load from Backend">
      {ModalHeader && ModalTitle && (
        <ModalHeader>
          <ModalTitle>Load Spec from Server</ModalTitle>
        </ModalHeader>
      )}
      {ModalBody && (
        <ModalBody>
          {isLoading && <div className="backend-specs-loading">Loading specs...</div>}
          {error && <div className="backend-specs-error">Error: {error}</div>}
          {!isLoading && !error && specsList.length === 0 && (
            <div className="backend-specs-empty">No specs available</div>
          )}
          {!isLoading && !error && specsList.length > 0 && (
            <div className="backend-specs-list">
              <table className="backend-specs-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Version</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {specsList.map((spec) => (
                    <tr
                      key={spec.id}
                      className={`backend-specs-item ${selectedSpecId === spec.id ? 'selected' : ''}`}
                      onClick={() => handleSpecClick(spec.id)}
                    >
                      <td>{spec.name || spec.title || `Spec ${spec.id}`}</td>
                      <td>{spec.version || '-'}</td>
                      <td>{spec.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      )}
      {ModalFooter && (
        <ModalFooter>
          <button type="button" className="btn btn-secondary" onClick={handleCancelClick}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleLoadClick}
            disabled={!selectedSpecId || isLoading}
          >
            Load
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

LoadFromBackendDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.shape({
    fetchSpecsList: PropTypes.func,
  }),
  backendSpecsSelectors: PropTypes.shape({
    selectSpecsList: PropTypes.func,
    selectSpecsListLoading: PropTypes.func,
    selectSpecsListError: PropTypes.func,
  }),
};

LoadFromBackendDialog.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
};

export default LoadFromBackendDialog;
